import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getBusinessOverview } from "./businessOverviewService";
import {
  FaChartPie,
  FaPrint,
  FaArrowLeft,
  FaDollarSign,
  FaShoppingCart,
  FaChartLine,
  FaExclamationCircle,
  FaUsers,
  FaTruck,
  FaUniversity,
  FaFilter,
  FaUndo,
  FaFileCsv,
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaWallet,
  FaCheckCircle
} from "react-icons/fa";
import "./BusinessOverview.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const BusinessOverviewPage = () => {
  const { canView, canExport } = usePermissions();

  if (!canView("overview")) {
    return <AccessDenied moduleName="Business Overview" action="view" />;
  }
  const navigate = useNavigate();

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Data
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasFetched = useRef(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      const data = await getBusinessOverview(filters);
      setReportData(data);
    } catch (err) {
      console.error("Business Overview fetch error:", err);
      setError("Failed to load Business Overview data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchReport();
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    hasFetched.current = false;
    setTimeout(() => {
      fetchReport();
    }, 10);
  };

  // Date range presets
  const handlePreset = (presetType) => {
    const now = new Date();
    let start = "";
    let end = now.toISOString().split("T")[0];

    if (presetType === "THIS_MONTH") {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      start = firstDay.toISOString().split("T")[0];
    } else if (presetType === "THIS_QUARTER") {
      const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
      const firstDay = new Date(now.getFullYear(), quarterMonth, 1);
      start = firstDay.toISOString().split("T")[0];
    } else if (presetType === "YTD") {
      const firstDay = new Date(now.getFullYear(), 0, 1);
      start = firstDay.toISOString().split("T")[0];
    }

    setStartDate(start);
    setEndDate(end);
    hasFetched.current = false;
    setTimeout(() => {
      fetchReport();
    }, 10);
  };

  const handlePrint = () => {
    window.print();
  };

  // CSV Export
  const handleExportCSV = () => {
    if (!reportData) return;
    const summary = reportData.summary || {};
    const monthly = reportData.monthly_trend || [];

    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "REKONIC - BUSINESS OVERVIEW REPORT\n";
    csvContent += `Generated On,${new Date().toLocaleDateString()}\n\n`;
    
    csvContent += "EXECUTIVE SUMMARY METRICS\n";
    csvContent += `Total Revenue,$${fmt(summary.total_revenue)}\n`;
    csvContent += `Total Purchases & Bills,$${fmt(summary.total_purchases)}\n`;
    csvContent += `Net Operating Income,$${fmt(summary.net_income)}\n`;
    csvContent += `Receivables Outstanding,$${fmt(summary.total_sales_outstanding)}\n`;
    csvContent += `Collection Rate,${summary.collection_rate_pct || 0}%\n`;
    csvContent += `Gross Margin,${summary.gross_margin_pct || 0}%\n\n`;

    csvContent += "MONTHLY FINANCIAL TREND\n";
    csvContent += "Month,Sales Revenue ($),Purchases ($),Profit ($),Net Cashflow ($)\n";
    monthly.forEach((r) => {
      csvContent += `"${r.month}",${r.sales},${r.purchases},${r.profit},${r.net}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Business_Overview_Report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const summary = reportData?.summary || {};
  const topCustomers = reportData?.top_customers || [];
  const topSuppliers = reportData?.top_suppliers || [];
  const bankAccounts = reportData?.bank_accounts || [];
  const monthlyTrend = reportData?.monthly_trend || [];

  // Max totals for customer/supplier progress bars
  const maxCustTotal = topCustomers.length > 0 ? Math.max(...topCustomers.map(c => Number(c.total || 0))) : 1;
  const maxSuppTotal = topSuppliers.length > 0 ? Math.max(...topSuppliers.map(s => Number(s.total || 0))) : 1;

  return (
    <div className="biz-overview-page">
      {/* Printable Header Notice */}
      <div className="biz-print-only-header">
        <h1>REKONIC ERP - EXECUTIVE BUSINESS OVERVIEW</h1>
        <p>Report Period: {startDate || "Beginning"} to {endDate || "Current Date"} | Generated: {new Date().toLocaleDateString()}</p>
      </div>

      {/* Top Header */}
      <div className="biz-overview-header">
        <div className="biz-title-group">
          <h2>
            <span className="biz-title-icon"><FaChartPie /></span>
            <span>Business Overview & Executive Dashboard</span>
          </h2>
          <p>Key financial metrics, revenue performance, purchase commitments, profitability & liquidity</p>
        </div>

        <div className="biz-header-actions">
          <button className="biz-btn biz-btn-export" onClick={handleExportCSV}>
            <FaFileCsv /> Export CSV
          </button>
          <button className="biz-btn biz-btn-print" onClick={handlePrint}>
            <FaPrint /> Print / PDF
          </button>
          <button className="biz-btn biz-btn-back" onClick={() => navigate("/dashboard")}>
            <FaArrowLeft /> Dashboard
          </button>
        </div>
      </div>

      {/* Filter Card & Range Presets */}
      <div className="biz-filter-card">
        <form onSubmit={handleFilterSubmit} className="biz-filter-form">
          <div className="biz-filter-inputs">
            <div className="biz-filter-group">
              <label><FaCalendarAlt className="me-1" /> Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="biz-filter-group">
              <label><FaCalendarAlt className="me-1" /> End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <button type="submit" className="biz-btn biz-btn-filter">
              <FaFilter /> Apply Filter
            </button>
            <button type="button" className="biz-btn biz-btn-reset" onClick={handleReset}>
              <FaUndo /> Reset
            </button>
          </div>
        </form>

        {/* Preset Shortcuts */}
        <div className="biz-presets-bar">
          <span className="biz-presets-label">Shortcuts:</span>
          <button type="button" className="biz-preset-chip" onClick={() => handlePreset("THIS_MONTH")}>
            This Month
          </button>
          <button type="button" className="biz-preset-chip" onClick={() => handlePreset("THIS_QUARTER")}>
            This Quarter
          </button>
          <button type="button" className="biz-preset-chip" onClick={() => handlePreset("YTD")}>
            Year to Date (YTD)
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="biz-card text-center p-5">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p style={{ color: "#64748b", fontWeight: "600", fontSize: "1rem" }}>Loading Executive Dashboard Data...</p>
        </div>
      ) : error ? (
        <div className="biz-card text-center p-5 text-danger">
          <FaExclamationCircle size={36} className="mb-3" />
          <p className="fw-semibold">{error}</p>
          <button className="biz-btn biz-btn-filter mt-2" onClick={fetchReport}>Retry</button>
        </div>
      ) : (
        <>
          {/* Primary Key Performance Indicators (KPIs) */}
          <div className="biz-kpi-grid">
            {/* Total Revenue */}
            <div className="biz-kpi-card revenue-accent">
              <div className="biz-kpi-top">
                <div className="biz-kpi-icon revenue">
                  <FaDollarSign />
                </div>
                <span className="biz-badge biz-badge-success">
                  <FaArrowUp /> Revenue
                </span>
              </div>
              <div className="biz-kpi-content">
                <span className="biz-kpi-title">Total Revenue</span>
                <span className="biz-kpi-value">${fmt(summary.total_revenue)}</span>
                <span className="biz-kpi-sub">{summary.sales_count || 0} Sales Invoices</span>
              </div>
            </div>

            {/* Total Purchases */}
            <div className="biz-kpi-card purchases-accent">
              <div className="biz-kpi-top">
                <div className="biz-kpi-icon purchases">
                  <FaShoppingCart />
                </div>
                <span className="biz-badge biz-badge-warning">
                  Expenses
                </span>
              </div>
              <div className="biz-kpi-content">
                <span className="biz-kpi-title">Total Purchases & Bills</span>
                <span className="biz-kpi-value">${fmt(summary.total_purchases)}</span>
                <span className="biz-kpi-sub">{summary.purchase_count || 0} Purchase Bills</span>
              </div>
            </div>

            {/* Net Operating Income */}
            <div className="biz-kpi-card income-accent">
              <div className="biz-kpi-top">
                <div className="biz-kpi-icon net-income">
                  <FaChartLine />
                </div>
                <span className={`biz-badge ${summary.net_income >= 0 ? "biz-badge-success" : "biz-badge-danger"}`}>
                  {summary.net_income >= 0 ? <FaArrowUp /> : <FaArrowDown />} Net Profit
                </span>
              </div>
              <div className="biz-kpi-content">
                <span className="biz-kpi-title">Net Operating Income</span>
                <span className={`biz-kpi-value ${summary.net_income >= 0 ? "biz-text-success" : "biz-text-danger"}`}>
                  ${fmt(summary.net_income)}
                </span>
                <span className="biz-kpi-sub">Revenue minus Purchase Expenses</span>
              </div>
            </div>

            {/* Receivables Outstanding */}
            <div className="biz-kpi-card outstanding-accent">
              <div className="biz-kpi-top">
                <div className="biz-kpi-icon outstanding">
                  <FaExclamationCircle />
                </div>
                <span className="biz-badge biz-badge-info">
                  Collection {summary.collection_rate_pct || 0}%
                </span>
              </div>
              <div className="biz-kpi-content">
                <span className="biz-kpi-title">Receivables Outstanding</span>
                <span className="biz-kpi-value">${fmt(summary.total_sales_outstanding)}</span>
                <span className="biz-kpi-sub">Outstanding Invoices Amount</span>
              </div>
            </div>
          </div>

          {/* Secondary Financial Metrics Strip */}
          <div className="biz-secondary-grid">
            <div className="biz-stat-box">
              <span className="biz-stat-icon text-success"><FaCheckCircle /></span>
              <div>
                <div className="biz-stat-label">Total Receipts Collected</div>
                <div className="biz-stat-num biz-text-success">${fmt(summary.total_receipts)}</div>
              </div>
            </div>
            <div className="biz-stat-box">
              <span className="biz-stat-icon text-danger"><FaArrowDown /></span>
              <div>
                <div className="biz-stat-label">Total Payments Made</div>
                <div className="biz-stat-num biz-text-danger">${fmt(summary.total_payments)}</div>
              </div>
            </div>
            <div className="biz-stat-box">
              <span className="biz-stat-icon text-primary"><FaWallet /></span>
              <div>
                <div className="biz-stat-label">Total Gross Profit</div>
                <div className="biz-stat-num text-primary">${fmt(summary.total_gross_profit)}</div>
              </div>
            </div>
            <div className="biz-stat-box">
              <span className="biz-stat-icon text-info"><FaChartLine /></span>
              <div>
                <div className="biz-stat-label">Gross Margin %</div>
                <div className="biz-stat-num text-dark">{summary.gross_margin_pct || 0}%</div>
              </div>
            </div>
          </div>

          {/* Monthly Trend Section */}
          <div className="biz-card mb-4">
            <div className="biz-card-header">
              <h3 className="biz-card-title">
                <FaChartLine style={{ color: "#0284c7" }} /> Monthly Financial Performance & Cashflow
              </h3>
              <span className="biz-card-subtitle">{monthlyTrend.length} Period Records</span>
            </div>
            <div className="biz-table-container">
              <table className="biz-table">
                <thead>
                  <tr>
                    <th>Period / Month</th>
                    <th className="biz-text-right">Sales Revenue ($)</th>
                    <th className="biz-text-right">Purchases ($)</th>
                    <th className="biz-text-right">Operating Profit ($)</th>
                    <th className="biz-text-right">Net Cashflow ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyTrend.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted p-4">
                        No monthly financial data recorded for the selected range.
                      </td>
                    </tr>
                  ) : (
                    monthlyTrend.map((row, idx) => (
                      <tr key={idx}>
                        <td><strong style={{ color: "#1e293b" }}>{row.month}</strong></td>
                        <td className="biz-text-right fw-semibold">${fmt(row.sales)}</td>
                        <td className="biz-text-right">${fmt(row.purchases)}</td>
                        <td className="biz-text-right biz-text-success fw-bold">${fmt(row.profit)}</td>
                        <td className={`biz-text-right fw-bold ${row.net >= 0 ? "biz-text-success" : "biz-text-danger"}`}>
                          ${fmt(row.net)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Grid for Top Customers & Top Suppliers with Progress Visuals */}
          <div className="biz-sections-grid">
            {/* Top Customers */}
            <div className="biz-card">
              <div className="biz-card-header">
                <h3 className="biz-card-title">
                  <FaUsers style={{ color: "#10b981" }} /> Top Customers by Sales Volume
                </h3>
              </div>
              <div className="biz-table-container">
                <table className="biz-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>Customer Name</th>
                      <th>Volume Share</th>
                      <th className="biz-text-right">Revenue ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCustomers.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted p-4">
                          No customer revenue data available.
                        </td>
                      </tr>
                    ) : (
                      topCustomers.map((cust, idx) => {
                        const pct = Math.round((Number(cust.total || 0) / maxCustTotal) * 100);
                        return (
                          <tr key={idx}>
                            <td className="text-muted">{idx + 1}</td>
                            <td><strong>{cust.name}</strong></td>
                            <td style={{ width: "35%" }}>
                              <div className="biz-progress-bar">
                                <div
                                  className="biz-progress-fill success"
                                  style={{ width: `${pct}%` }}
                                ></div>
                              </div>
                            </td>
                            <td className="biz-text-right biz-text-success fw-bold">${fmt(cust.total)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top Suppliers */}
            <div className="biz-card">
              <div className="biz-card-header">
                <h3 className="biz-card-title">
                  <FaTruck style={{ color: "#ef4444" }} /> Top Suppliers by Purchase Volume
                </h3>
              </div>
              <div className="biz-table-container">
                <table className="biz-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>Supplier Name</th>
                      <th>Volume Share</th>
                      <th className="biz-text-right">Purchases ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSuppliers.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted p-4">
                          No supplier purchase data available.
                        </td>
                      </tr>
                    ) : (
                      topSuppliers.map((supp, idx) => {
                        const pct = Math.round((Number(supp.total || 0) / maxSuppTotal) * 100);
                        return (
                          <tr key={idx}>
                            <td className="text-muted">{idx + 1}</td>
                            <td><strong>{supp.name}</strong></td>
                            <td style={{ width: "35%" }}>
                              <div className="biz-progress-bar">
                                <div
                                  className="biz-progress-fill danger"
                                  style={{ width: `${pct}%` }}
                                ></div>
                              </div>
                            </td>
                            <td className="biz-text-right biz-text-danger fw-bold">${fmt(supp.total)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Bank & Liquid Cash Balances */}
          {bankAccounts.length > 0 && (
            <div className="biz-card mt-4">
              <div className="biz-card-header">
                <h3 className="biz-card-title">
                  <FaUniversity style={{ color: "#0284c7" }} /> Bank Accounts & Liquid Financial Balances
                </h3>
                <span className="biz-card-subtitle">{bankAccounts.length} Active Accounts</span>
              </div>
              <div className="biz-bank-grid">
                {bankAccounts.map((b) => (
                  <div className="biz-bank-card" key={b.id || b.account_number}>
                    <div className="biz-bank-card-icon">
                      <FaUniversity />
                    </div>
                    <div className="biz-bank-details">
                      <div className="biz-bank-name">{b.bank_name || b.account_name}</div>
                      <div className="biz-bank-acc">{b.account_name} &bull; {b.account_number || "Main Account"}</div>
                    </div>
                    <div className="biz-bank-bal-group">
                      <span className="biz-bank-bal-label">Current Balance</span>
                      <span className="biz-bank-bal">${fmt(b.balance)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BusinessOverviewPage;
